import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { Link } from 'react-router-dom'
import { CheckCircle, AlertTriangle, MessageCircle, X } from 'lucide-react'
import { http } from '../../shared/api/http'
import { useAuthStore, useAuth } from '../../shared/auth/store'
import { logger } from '../../shared/utils/logger'

interface ProfileData {
  name: string
  surname: string
  email: string
  phone?: string
  company_name?: string
  company_email?: string
  // Поля от бэкенда
  first_name?: string
  last_name?: string
  contact_id?: string
  company_id?: string
  // Telegram данные
  telegram_id?: string
  has_telegram_id?: boolean
}

interface PasswordData {
  current_password: string
  new_password: string
  confirm_password: string
}

export function ProfilePage() {
  const [user, setUser] = useState<ProfileData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'profile' | 'password'>('profile')
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [botUsername, setBotUsername] = useState('fdsf43rfa_bot') // По умолчанию, обновляется из конфига

  const profileForm = useForm<ProfileData>()
  const passwordForm = useForm<PasswordData>()
  
  // Получаем режим авторизации из store
  const authMode = useAuthStore(state => state.mode)

  // Загружаем данные пользователя и конфигурацию бота
  useEffect(() => {
    loadUserData()
    loadBotConfig()
  }, [])

  const loadBotConfig = async () => {
    try {
      const response = await http.get('/web_auth/bot-config')
      if (response.data.success) {
        setBotUsername(response.data.bot_username)
      }
    } catch (error) {
      logger.error('Ошибка загрузки конфигурации бота:', error)
      // Оставляем значение по умолчанию
    }
  }

  const disconnectTelegram = async () => {
    try {
      const response = await http.post('/web_auth/disconnect-telegram')
      if (response.data.success) {
        setMessage('Telegram успешно отключен!')
        // Обновляем данные пользователя
        await loadUserData()
      } else {
        setError(response.data.message || 'Ошибка при отключении Telegram')
      }
    } catch (error) {
      logger.error('Ошибка отключения Telegram:', error)
      setError('Произошла ошибка при отключении Telegram')
    }
  }

  const loadUserData = async () => {
    try {
      setIsLoading(true)
      const response = await http.get('/web_auth/profile')

      
      // Исправлено: данные профиля в response.data.data
      const userData = response.data.data || response.data

      setUser(userData)
      
      // Заполняем форму текущими данными
      const formData = {
        name: userData.first_name || userData.name || '',
        surname: userData.last_name || userData.surname || '',
        email: userData.email || '',
        phone: userData.phone || '',
        company_name: userData.company_name || '',
        company_email: userData.company_email || ''
      }
      

      profileForm.reset(formData)
    } catch (error) {
      logger.error('Ошибка загрузки профиля:', error)
      setError('Не удалось загрузить данные профиля')
    } finally {
      setIsLoading(false)
    }
  }

  const onProfileSubmit = async (data: ProfileData) => {
    try {
      setMessage('')
      setError('')
      
      // Исключаем поля компании из отправки (они только для чтения)
      const { company_name, company_email, ...profileData } = data
      logger.log('💼 Обновление профиля:', profileData)
      
      const response = await http.put('/web_auth/profile', profileData)
      
      if (response.data.success) {
        setMessage('Профиль успешно обновлен!')
        setUser({ ...user, ...data })
        // Синхронизируем глобального пользователя для ProfileDropdown
        const update = {
          first_name: data.name || user?.first_name,
          last_name: data.surname || user?.last_name,
          email: data.email || user?.email,
          phone: data.phone || user?.phone,
        }
        try {
          useAuthStore.getState().updateUser(update as any)
        } catch {}
        
        // Обновляем данные в форме
        profileForm.reset(data)
      } else {
        setError(response.data.message || 'Ошибка при обновлении профиля')
      }
    } catch (error: any) {
      logger.error('Ошибка обновления профиля:', error)
      setError(error.response?.data?.message || 'Ошибка при обновлении профиля')
    }
  }

  const onPasswordSubmit = async (data: PasswordData) => {
    try {
      setMessage('')
      setError('')

      // Проверяем, что новые пароли совпадают
      if (data.new_password !== data.confirm_password) {
        setError('Новые пароли не совпадают')
        return
      }

      logger.log('🔐 Смена пароля')
      
      const response = await http.put('/web_auth/change-password', {
        current_password: data.current_password,
        new_password: data.new_password
      })
      
      if (response.data.success) {
        setMessage('✅ Пароль успешно изменен!')
        passwordForm.reset()
      } else {
        setError(response.data.message || 'Ошибка при смене пароля')
      }
    } catch (error: any) {
      logger.error('Ошибка смены пароля:', error)
      setError(error.response?.data?.message || 'Ошибка при смене пароля')
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center px-4">
        {/* Компактный индикатор загрузки */}
        <div className="flex items-center gap-3 bg-white/80 backdrop-blur-sm rounded-full px-6 py-3 shadow-lg border border-white/20">
          <div className="animate-spin w-5 h-5 border-2 border-blue-200 border-t-blue-500 rounded-full"></div>
          <span className="text-sm font-medium text-gray-700">Загрузка...</span>
        </div>
      </div>
    )
  }

  // Современные стили удалены в пользу Tailwind CSS классов

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <div className="container mx-auto px-4 py-6 max-w-2xl">
        {/* Кнопка "Назад" */}
        <div className="mb-4">
          <Link 
            to="/orders" 
            className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 hover:bg-white backdrop-blur-sm border border-gray-200/50 hover:border-gray-300/50 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 text-gray-700 hover:text-gray-900 text-sm font-medium"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span>Назад к каталогу</span>
          </Link>
        </div>
        {/* Стили для анимаций */}
        <style>{`
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translate3d(0, 40px, 0);
            }
            to {
              opacity: 1;
              transform: translate3d(0, 0, 0);
            }
          }
          
          .animate-fadeInUp {
            animation: fadeInUp 0.6s ease-out;
          }
          
          .glassmorphism {
            background: rgba(255, 255, 255, 0.25);
            backdrop-filter: blur(10px);
            border: 1px solid rgba(255, 255, 255, 0.18);
          }
        `}</style>

        {/* Компактный заголовок профиля */}
        <div className="animate-fadeInUp mb-4">
          <div className="bg-white rounded-xl shadow-sm p-3 border border-gray-100">
            <div className="flex items-center gap-3">
              {/* Компактный аватар */}
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-sm flex-shrink-0">
                <span className="text-white text-lg font-bold">
                  {user?.first_name?.[0] || user?.name?.[0] || '👤'}
                </span>
              </div>
              
              {/* Компактная информация */}
              <div className="flex-1 min-w-0">
                <h1 className="text-lg font-bold text-gray-900 truncate">
                  {user?.first_name || user?.name || 'Пользователь'} {user?.last_name || user?.surname || ''}
                </h1>
                {user?.company_name ? (
                  <p className="text-xs text-green-600 truncate">🏢 {user.company_name}</p>
                ) : (
                  <p className="text-xs text-gray-600 truncate">{user?.email}</p>
                )}
              </div>
              
              {/* Статус онлайн */}
              <div className="flex items-center gap-1 text-xs text-gray-500">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="hidden sm:inline">Онлайн</span>
              </div>
            </div>
          </div>
        </div>

        {/* Компактные вкладки */}
        <div className="animate-fadeInUp mb-4">
          <div className="bg-white rounded-lg p-1 shadow-sm border border-gray-100">
            <div className="flex gap-1">
              <button
                onClick={() => setActiveTab('profile')}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md font-medium transition-all duration-200 ${
                  activeTab === 'profile'
                    ? 'bg-blue-500 text-white shadow-sm'
                    : 'text-gray-600 hover:text-blue-600 hover:bg-gray-50'
                }`}
              >
                <span className="text-sm">📝</span>
                <span className="text-xs">Данные</span>
              </button>
              <button
                onClick={() => setActiveTab('password')}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md font-medium transition-all duration-200 ${
                  activeTab === 'password'
                    ? 'bg-blue-500 text-white shadow-sm'
                    : 'text-gray-600 hover:text-blue-600 hover:bg-gray-50'
                }`}
              >
                <span className="text-sm">🔐</span>
                <span className="text-xs">Пароль</span>
              </button>
            </div>
          </div>
        </div>

        {/* Компактные уведомления */}
        {message && (
          <div className="animate-fadeInUp mb-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
              <span className="text-green-500 text-sm">✅</span>
              <p className="text-green-800 text-sm font-medium flex-1">{message}</p>
            </div>
          </div>
        )}

        {error && (
          <div className="animate-fadeInUp mb-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
              <span className="text-red-500 text-sm">❌</span>
              <p className="text-red-800 text-sm font-medium flex-1">{error}</p>
            </div>
          </div>
        )}

        {/* Компактная форма профиля */}
        {activeTab === 'profile' && (
          <div className="animate-fadeInUp">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100">
              {/* Компактный заголовок */}
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-100">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <span className="text-blue-500">✏️</span>
                  Редактировать профиль
                </h2>
              </div>

              {/* Компактное содержимое формы */}
              <div className="p-4">
                <form onSubmit={profileForm.handleSubmit(onProfileSubmit)}>
                  {/* Компактные поля */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Имя *</label>
                      <input
                        {...profileForm.register('name', { 
                          required: 'Имя обязательно',
                          minLength: { value: 2, message: 'Минимум 2 символа' }
                        })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                        placeholder="Имя"
                      />
                      {profileForm.formState.errors.name && (
                        <p className="text-red-500 text-xs mt-1">{profileForm.formState.errors.name.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Фамилия *</label>
                      <input
                        {...profileForm.register('surname', { 
                          required: 'Фамилия обязательна',
                          minLength: { value: 2, message: 'Минимум 2 символа' }
                        })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                        placeholder="Фамилия"
                      />
                      {profileForm.formState.errors.surname && (
                        <p className="text-red-500 text-xs mt-1">{profileForm.formState.errors.surname.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    {/* Email */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Email *</label>
                      <input
                        {...profileForm.register('email', { 
                          required: 'Email обязателен',
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: 'Некорректный email'
                          }
                        })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                        type="email"
                        placeholder="email@company.com"
                      />
                      {profileForm.formState.errors.email && (
                        <p className="text-red-500 text-xs mt-1">{profileForm.formState.errors.email.message}</p>
                      )}
                    </div>

                    {/* Телефон */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Телефон</label>
                      <input
                        {...profileForm.register('phone', {
                          validate: (value) => {
                            if (!value || value.trim() === '') return true; // Пустое поле допустимо
                            
                            // Удаляем все символы кроме цифр и +
                            const cleanPhone = value.replace(/[^\d\+]/g, '');
                            
                            // Проверяем формат: + в начале (опционально) + от 7 до 15 цифр
                            return /^\+?\d{7,15}$/.test(cleanPhone) || 'Некорректный формат для номера телефона';
                          }
                        })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                        type="tel"
                        placeholder="+7 (999) 123-45-67"
                      />
                      {profileForm.formState.errors.phone && (
                        <p className="text-red-500 text-xs mt-1">{profileForm.formState.errors.phone.message}</p>
                      )}
                    </div>
                  </div>

                  {/* Данные компании */}
                  <div className="mb-4">
                    <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                      <span className="text-blue-500">🏢</span>
                      Данные компании
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Название компании */}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Название компании</label>
                        <input
                          {...profileForm.register('company_name')}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-700 text-sm cursor-not-allowed"
                          placeholder="ООО Компания"
                          readOnly
                        />
                      </div>

                      {/* Email компании */}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Email компании</label>
                        <input
                          {...profileForm.register('company_email')}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-700 text-sm cursor-not-allowed"
                          type="email"
                          placeholder="info@company.com"
                          readOnly
                        />
                      </div>
                    </div>
                  </div>

                  {/* Telegram уведомления */}
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-3 rounded-lg border border-blue-100">
                    <div className="flex items-center gap-2 mb-2">
                      <MessageCircle className="w-4 h-4 text-blue-600" />
                      <h3 className="text-sm font-semibold text-gray-900">Telegram уведомления</h3>
                    </div>
                    
                    {user?.has_telegram_id ? (
                      <div className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-green-800">Подключено</p>
                          <p className="text-xs text-green-600">Получаете уведомления о заказах</p>
                        </div>
                        <button
                          onClick={() => {
                            // Проверяем режим авторизации
                            if (authMode === 'telegram') {
                              alert('Отключение Telegram доступно только через браузерную версию.\n\nОткройте личный кабинет в браузере для управления настройками.')
                              return
                            }
                            
                            if (window.confirm('Вы уверены, что хотите отключить Telegram?\n\n• Вы перестанете получать уведомления\n• Потеряете доступ к мини-приложению\n• Останется только доступ через браузер')) {
                              disconnectTelegram()
                            }
                          }}
                          className={`p-1 rounded transition-colors ${
                            authMode === 'telegram' 
                              ? 'text-gray-300 cursor-not-allowed' 
                              : 'text-red-400 hover:text-red-600 hover:bg-red-50'
                          }`}
                          title={authMode === 'telegram' ? 'Доступно только в браузере' : 'Отключить Telegram'}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <AlertTriangle className="w-4 h-4 text-yellow-500" />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-yellow-800">Не подключено</p>
                            
                          </div>
                        </div>
                        
                        <div className="bg-white p-2 rounded-lg border border-gray-200">
                          <p className="text-xs text-gray-700 font-medium mb-1">Преимущества:</p>
                          <ul className="text-xs text-gray-600 space-y-0.5">
                            <li>• Push-уведомления о заказах</li>
                            <li>• Быстрая связь с менеджером</li>
                            <li>• Статус в реальном времени</li>
                          </ul>
                          
                          <div className="mt-2 pt-2 border-t border-gray-100">
                            <button
                              onClick={() => {
                                // Просто открываем бота
                                const telegramUrl = `https://t.me/${botUsername}`;
                                window.open(telegramUrl, '_blank');
                              }}
                                                          className="w-full bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium py-2 px-3 rounded-lg transition-colors flex items-center justify-center gap-1"
                          >
                            <MessageCircle className="w-4 h-4" />
                            <span>Перейти в бота</span>
                          </button>
                            <p className="text-xs text-gray-500 mt-1 text-center">
                              Войдите через Telegram мини-апп с теми же данными
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Компактная кнопка сохранения */}
                  <button
                    type="submit"
                    disabled={profileForm.formState.isSubmitting}
                    className={`w-full py-2.5 px-4 rounded-lg font-medium text-white transition-colors flex items-center justify-center gap-2 text-sm ${
                      profileForm.formState.isSubmitting
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-blue-500 hover:bg-blue-600'
                    }`}
                  >
                    {profileForm.formState.isSubmitting ? (
                      <>
                        <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                        <span>Сохранение...</span>
                      </>
                    ) : (
                      <>
                        <span>💾</span>
                        <span>Сохранить</span>
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Компактная форма смены пароля */}
        {activeTab === 'password' && (
          <div className="animate-fadeInUp">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100">
              {/* Компактный заголовок */}
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-100">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <span className="text-amber-500">🔐</span>
                  Изменить пароль
                </h2>
              </div>

              {/* Компактное содержимое формы */}
              <div className="p-4">
                <form onSubmit={passwordForm.handleSubmit(onPasswordSubmit)}>
                  <div className="space-y-4 mb-4">
                    {/* Текущий пароль */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Текущий пароль *</label>
                      <input
                        {...passwordForm.register('current_password', { 
                          required: 'Введите текущий пароль'
                        })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-amber-500 focus:border-amber-500 transition-colors text-sm"
                        type="password"
                        placeholder="Текущий пароль"
                      />
                      {passwordForm.formState.errors.current_password && (
                        <p className="text-red-500 text-xs mt-1">{passwordForm.formState.errors.current_password.message}</p>
                      )}
                    </div>

                    {/* Новый пароль */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Новый пароль *</label>
                      <input
                        {...passwordForm.register('new_password', { 
                          required: 'Введите новый пароль',
                          minLength: { value: 6, message: 'Минимум 6 символов' }
                        })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-amber-500 focus:border-amber-500 transition-colors text-sm"
                        type="password"
                        placeholder="Новый пароль"
                      />
                      {passwordForm.formState.errors.new_password && (
                        <p className="text-red-500 text-xs mt-1">{passwordForm.formState.errors.new_password.message}</p>
                      )}
                    </div>

                    {/* Подтверждение пароля */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Подтвердите пароль *</label>
                      <input
                        {...passwordForm.register('confirm_password', { 
                          required: 'Подтвердите пароль',
                          validate: (value) => {
                            const newPassword = passwordForm.getValues('new_password')
                            return value === newPassword || 'Пароли не совпадают'
                          }
                        })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-amber-500 focus:border-amber-500 transition-colors text-sm"
                        type="password"
                        placeholder="Повторите пароль"
                      />
                      {passwordForm.formState.errors.confirm_password && (
                        <p className="text-red-500 text-xs mt-1">{passwordForm.formState.errors.confirm_password.message}</p>
                      )}
                    </div>
                  </div>

                  {/* Компактная кнопка изменения пароля */}
                  <button
                    type="submit"
                    disabled={passwordForm.formState.isSubmitting}
                    className={`w-full py-2.5 px-4 rounded-lg font-medium text-white transition-colors flex items-center justify-center gap-2 text-sm ${
                      passwordForm.formState.isSubmitting
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-amber-500 hover:bg-amber-600'
                    }`}
                  >
                    {passwordForm.formState.isSubmitting ? (
                      <>
                        <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                        <span>Изменение...</span>
                      </>
                    ) : (
                      <>
                        <span>🔐</span>
                        <span>Изменить пароль</span>
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
